import { useState } from "react";
import axios from "axios";
import { Lock, Mail, Phone, Instagram, MapPin, RefreshCw } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Admin() {
  const [key, setKey] = useState("");
  const [authed, setAuthed] = useState(false);
  const [rows, setRows] = useState([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const load = async (adminKey) => {
    setLoading(true);
    setError("");
    try {
      const res = await axios.get(`${API}/contact`, { headers: { "x-admin-key": adminKey } });
      setRows(res.data);
      setAuthed(true);
    } catch (e) {
      setError("Chiave non valida o errore di connessione.");
      setAuthed(false);
    } finally {
      setLoading(false);
    }
  };

  if (!authed) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center px-5">
        <div className="w-full max-w-sm rounded-3xl border border-white/10 bg-[#0b0b0b] p-8">
          <div className="flex items-center gap-3 text-white">
            <Lock className="text-teal-light" size={22} />
            <h1 className="font-display text-xl">Area Admin</h1>
          </div>
          <p className="mt-2 text-sm text-slate-400">Inserisci la chiave per visualizzare le richieste.</p>
          <div className="mt-6 space-y-3">
            <Label className="text-xs uppercase tracking-wider text-slate-400">Admin key</Label>
            <Input
              data-testid="admin-key-input"
              type="password"
              value={key}
              onChange={(e) => setKey(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && load(key)}
              className="bg-[#101010] border-white/10 text-white"
              placeholder="••••••••"
            />
            {error && <p className="text-sm text-red-400" data-testid="admin-error">{error}</p>}
            <button
              data-testid="admin-login-btn"
              onClick={() => load(key)}
              disabled={loading}
              className="w-full rounded-full bg-teal-light py-3 text-sm font-semibold text-[#050505] hover:bg-white transition-colors disabled:opacity-60"
            >
              {loading ? "Verifica..." : "Accedi"}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] text-slate-200">
      <div className="max-w-6xl mx-auto px-5 lg:px-8 py-12">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-display text-3xl text-white">Richieste di consulenza</h1>
            <p className="text-sm text-slate-400 mt-1">{rows.length} richieste totali</p>
          </div>
          <button data-testid="admin-refresh" onClick={() => load(key)} className="inline-flex items-center gap-2 rounded-full border border-white/15 px-4 py-2 text-sm hover:bg-white/10 transition-colors">
            <RefreshCw size={15} /> Aggiorna
          </button>
        </div>

        <div className="mt-8 grid gap-4">
          {rows.length === 0 && <p className="text-slate-500" data-testid="admin-empty">Nessuna richiesta al momento.</p>}
          {rows.map((r) => (
            <div key={r.id} data-testid={`admin-row-${r.id}`} className="rounded-2xl border border-white/10 bg-[#0b0b0b] p-6">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <h3 className="font-display text-lg text-white">{r.nome_cognome} <span className="text-slate-500 text-sm">— {r.attivita}</span></h3>
                <div className="flex items-center gap-2">
                  <Badge className="bg-teal/20 text-teal-light border-0">{r.piano_interesse}</Badge>
                  <span className="text-xs text-slate-500">{new Date(r.created_at).toLocaleString("it-IT")}</span>
                </div>
              </div>
              <div className="mt-4 grid sm:grid-cols-2 lg:grid-cols-4 gap-3 text-sm">
                <span className="flex items-center gap-2 text-slate-300"><MapPin size={14} className="text-teal-light" /> {r.settore} · {r.citta}</span>
                <a href={`tel:${r.telefono}`} className="flex items-center gap-2 text-slate-300 hover:text-teal-light"><Phone size={14} className="text-teal-light" /> {r.telefono}</a>
                <a href={`mailto:${r.email}`} className="flex items-center gap-2 text-slate-300 hover:text-teal-light"><Mail size={14} className="text-teal-light" /> {r.email}</a>
                {r.instagram && <span className="flex items-center gap-2 text-slate-300"><Instagram size={14} className="text-teal-light" /> {r.instagram}</span>}
              </div>
              {r.obiettivi && <p className="mt-4 text-sm text-slate-400 border-t border-white/5 pt-3">{r.obiettivi}</p>}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
